import { Image, ImageSourcePropType } from "react-native";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import { Row, StatusBadge, colors } from "@louise/moon-ui";
import { GroupedCard } from "../components";
import { ICONS } from "../icons";
import type { YouScreenAction } from "../types";
import { use2FAStatus } from "@src/packages/auth/hooks/use2FA";

const ICON_SIZE = 28;
const ROW_TITLE_STYLE = {
  fontSize: 15,
  lineHeight: 20,
  fontWeight: "500" as const,
  color: EMBER.colors.text,
};
const ROW_SUBTITLE_STYLE = {
  fontSize: 12,
  lineHeight: 17,
  color: EMBER.colors.textFaint,
};

function AccountActionIcon({ source }: { source: ImageSourcePropType }) {
  return (
    <Image
      source={source}
      resizeMode="contain"
      style={{ width: ICON_SIZE, height: ICON_SIZE }}
    />
  );
}

type Props = {
  dispatch: React.Dispatch<YouScreenAction>;
};

/** A second, full-row path to the same confirmation dialog at the bottom of You. */
export function LogoutShortcut({ onPress }: { onPress: () => void }) {
  const t = useTranslations();

  return (
    <GroupedCard>
      <Row
        surface="plain"
        leading={<AccountActionIcon source={ICONS.door} />}
        leadingSize={ICON_SIZE}
        title={t("you.account_footer.logout_label")}
        subtitle={t("you.account_footer.logout_description")}
        onPress={onPress}
        action="you.account_footer.logout_label"
        accessibilityLabel={t("you.account_footer.logout_label")}
        chevron
        gap={14}
        paddingHorizontal={16}
        paddingVertical={14}
        borderRadius={0}
        titleStyle={ROW_TITLE_STYLE}
        subtitleStyle={ROW_SUBTITLE_STYLE}
        style={{ borderRadius: 0 }}
      />
    </GroupedCard>
  );
}

export default function AccountFooter({ dispatch }: Props) {
  const t = useTranslations();
  const { data: twoFAStatus } = use2FAStatus();
  // `enabled` is the backend's gate flag. A passkey registered for a separate
  // login flow should not make this row claim that account 2FA is enabled.
  const isEnabled = twoFAStatus?.enabled ?? false;
  const hasTotp = twoFAStatus?.totpEnrolled ?? false;
  const hasPasskey = twoFAStatus?.passkeyEnrolled ?? false;
  const canManageCurrentFactor = isEnabled && (hasTotp || hasPasskey);
  // A person who has not enrolled 2FA needs this row to begin setup.
  // An account enabled through a legacy factor that this surface cannot manage
  // stays informative rather than sending them into an unsupported flow.
  const canOpenSecurityFlow = !isEnabled || canManageCurrentFactor;
  const securityDescription = isEnabled
    ? hasTotp && hasPasskey
      ? t("you.account_footer.two_factor_both_enabled_description")
      : hasPasskey
        ? t("you.account_footer.two_factor_passkey_enabled_description")
        : hasTotp
          ? t("you.account_footer.two_factor_totp_enabled_description")
          : t("you.account_footer.two_factor_enabled_description")
    : t("you.account_footer.two_factor_disabled_description");

  return (
    <>
      <GroupedCard style={{ marginBottom: 12 }}>
        <Row
          surface="plain"
          leading={<AccountActionIcon source={ICONS.padlock} />}
          leadingSize={ICON_SIZE}
          title={t("you.account_footer.two_factor_label")}
          subtitle={securityDescription}
          trailing={
            <StatusBadge
              label={t(
                isEnabled
                  ? "you.account_footer.enabled"
                  : "you.account_footer.disabled",
              )}
              tone={isEnabled ? "green" : "neutral"}
            />
          }
          onPress={
            canOpenSecurityFlow
              ? () =>
                  dispatch({
                    type: "SHOW_MODAL",
                    modal: isEnabled ? "disable-2fa" : "setup-2fa",
                  })
              : undefined
          }
          action="you.account_footer.two_factor_label"
          chevron={canOpenSecurityFlow}
          accessibilityLabel={`${t("you.account_footer.two_factor_label")}: ${t(
            isEnabled
              ? "you.account_footer.enabled"
              : "you.account_footer.disabled",
          )}`}
          gap={14}
          paddingHorizontal={16}
          paddingVertical={14}
          borderRadius={0}
          titleStyle={ROW_TITLE_STYLE}
          subtitleStyle={ROW_SUBTITLE_STYLE}
          style={{ borderRadius: 0 }}
        />
        <Row
          surface="plain"
          leading={<AccountActionIcon source={ICONS.door} />}
          leadingSize={ICON_SIZE}
          title={t("you.account_footer.logout_label")}
          subtitle={t("you.account_footer.logout_description")}
          onPress={() => dispatch({ type: "SHOW_MODAL", modal: "logout" })}
          action="you.account_footer.logout_label"
          accessibilityLabel={t("you.account_footer.logout_label")}
          gap={14}
          paddingHorizontal={16}
          paddingVertical={14}
          borderRadius={0}
          titleStyle={ROW_TITLE_STYLE}
          subtitleStyle={ROW_SUBTITLE_STYLE}
          style={{
            borderRadius: 0,
            borderTopWidth: 1,
            borderTopColor: colors.border,
          }}
        />
      </GroupedCard>

      <GroupedCard style={{ marginBottom: 12 }}>
        <Row
          surface="plain"
          leading={<AccountActionIcon source={ICONS.trash} />}
          leadingSize={ICON_SIZE}
          title={t("you.account_footer.deactivate_label")}
          subtitle={t("you.account_footer.deactivate_description")}
          onPress={() => dispatch({ type: "SHOW_MODAL", modal: "delete" })}
          action="you.account_footer.deactivate_label"
          accessibilityLabel={t("you.account_footer.deactivate_label")}
          gap={14}
          paddingHorizontal={16}
          paddingVertical={14}
          borderRadius={0}
          titleStyle={{ ...ROW_TITLE_STYLE, color: EMBER.colors.coral }}
          subtitleStyle={ROW_SUBTITLE_STYLE}
          style={{ borderRadius: 0 }}
        />
      </GroupedCard>
    </>
  );
}
